import { BaseLayout } from "../layouts";
import { Box, Button, Typography } from "@mui/material";
import { ConsentContext, Container, NextPageWithLayout } from "@akte-mord/common";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../next-i18next.config";
import React from "react";
import type { GetStaticProps } from "next";

const PrivacyPolicy: NextPageWithLayout = () => {
  const { openPreferences } = React.useContext(ConsentContext);

  return (
    <Container>
      <Typography variant="h1" sx={{ pb: 6 }}>
        Politique de confidentialité
      </Typography>
      <Typography variant="body1" paragraph>
        Nous traitons uniquement vos données à caractère personnel (ci-après dénommées le plus souvent
        «&nbsp;Données&nbsp;») selon les besoins et dans le but de proposer un site internet fonctionnel
        et convivial, notamment dans son contenu et en termes de services proposés.
      </Typography>
      <Typography variant="body1" paragraph>
        En vertu de l’art. 4, paragraphe 1, du règlement (UE) 2016/679, le règlement de base sur la
        protection des données (ci-après dénommé «&nbsp;RGPD&nbsp;») correspond à toute opération ou tout
        ensemble d’opérations effectuées avec ou sans procédés automatisés et appliqués à des données ou
        des ensembles de données à caractère personnel, telles que la collecte, l’enregistrement,
        l’organisation, la structuration, la conservation, l’adaptation ou la modification, la lecture,
        la consultation, l’utilisation, la communication par transmission, la diffusion ou tout autre
        moyen permettant le rapprochement ou l’interconnexion, la limitation, l’effacement ou la
        destruction.
      </Typography>
      <Typography variant="body1" paragraph>
        Notre politique de confidentialité vous fournit principalement des informations relatives à la
        nature, la portée, l’objectif, la durée et la base légale du traitement des données à caractère
        personnel, dans la mesure où nous décidons des objectifs et des moyens du traitement, seuls ou
        avec d’autres partenaires. Nous vous informons également des partenaires tiers avec lesquels nous
        travaillons à des fins d’optimisation et d’augmentation de la qualité d’utilisation, lorsque ces
        derniers traitent les données sous leur propre responsabilité.
      </Typography>
      <Typography variant="body1" paragraph>
        Notre politique de confidentialité est structurée comme suit&nbsp;:
      </Typography>
      <Box component="ol" sx={{ pl: 2 }}>
        <Box component="li">Informations nous concernant en tant que responsables</Box>
        <Box component="li">Droits de l’utilisateur et des parties concernées</Box>
        <Box component="li">Informations relatives au traitement des données</Box>
      </Box>
      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        1. Informations nous concernant en tant que responsables
      </Typography>
      <Typography variant="body1" paragraph>
        En termes de protection des données, le fournisseur responsable de ce site internet
        est&nbsp;: Bischoff, Kauffmann & Otani GbR, Hohenzollerndamm 152, 14199 Berlin, Allemagne.
      </Typography>
      <Typography variant="body1" paragraph>
        Bischoff, Kauffmann & Otani GbR est représentée par les associés gérants Florian Bischoff, Jens
        Kauffmann et Takeshi Otani et peut être contactée comme suit : Tél. : +49 (0) 30 25 29 21 51,
        e-mail : mail@cold-case.fr, site web : www.cold-case.fr.
      </Typography>
      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        2. Droits de l’utilisateur et des parties concernées
      </Typography>
      <Typography variant="body1" paragraph>
        En ce qui concerne le traitement des données décrit ci-dessous plus en détail, les utilisateurs
        et les personnes concernées ont le droit&nbsp;:
      </Typography>
      <Box component="ul" sx={{ pl: 3, py: 0 }}>
        <Box component="li">
          D’obtenir la confirmation que les données sont traitées, de savoir quels types de données sont
          traités, d’obtenir des informations supplémentaires relatives au traitement des données et des
          copies des données (voir également l’Art. 15 du RGPD)&nbsp;;
        </Box>
        <Box component="li">
          De pouvoir corriger ou compléter des données erronées ou incomplètes (voir l’Art. 16 du
          RGPD)&nbsp;;
        </Box>
        <Box component="li">
          De pouvoir supprimer immédiatement les données les concernant (voir également l’Art. 17 du
          RGPD), ou, de savoir si un traitement supplémentaire, conformément à l’Art. 17, paragraphe 3 du
          RGPD, est requis. De pouvoir limiter le traitement, conformément à l’Art. 18 du RGPD&nbsp;;
        </Box>
        <Box component="li">
          De pouvoir obtenir les données les concernant et qu’ils ont fournies, et de savoir si ces
          données ont été transmises à d’autres prestataires/autorités (voir également l’Art. 20 du
          RGPD)&nbsp;;
        </Box>
        <Box component="li">
          De déposer une plainte auprès des autorités de contrôle s’ils considèrent que le fournisseur
          viole la règlementation en matière de protection des données (voir également l’Art. 77 du
          RGPD).
        </Box>
      </Box>
      <Typography variant="body1" paragraph>
        En outre, le fournisseur est tenu d’informer tous les destinataires, auprès desquels les données
        ont été divulguées, de toute modification, suppression de données ou restriction du traitement,
        et ce, conformément à l’article 16, 17 paragraphes 1, 18 du RGPD. Cependant, cette obligation ne
        s’applique toutefois pas si la communication des informations s’avère impossible ou implique des
        efforts disproportionnés. L’utilisateur a néanmoins le droit d’obtenir des informations sur ces
        destinataires.
      </Typography>
      <Typography fontWeight={600} variant="body1" paragraph>
        De même, en vertu de l’Art. 21 du RGPD, les utilisateurs et les personnes concernées peuvent
        s’opposer au traitement ultérieur de leurs données si les données sont traitées par le
        fournisseur conformément à l’Art. 6, paragraphe 1, point f du RGPD. Il est en particulier
        autorisé de s’opposer au traitement des données à des fins de publicité directe.
      </Typography>
      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        3. Informations relatives au traitement des données
      </Typography>
      <Typography variant="body1" paragraph>
        Vos données traitées lors de l’utilisation de notre site internet seront supprimées ou bloquées
        dès lors que la raison de leur conservation n’a plus lieu d’être, que leur suppression n’entre
        pas en conflit avec les exigences légales en matière de conservation. Aucune autre information ne
        sera fournie ultérieurement relativement aux méthodes de traitement individuelles.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.1 Cookie Manager
      </Typography>
      <Typography variant="body1" paragraph>
        Pour obtenir le consentement à l’utilisation de cookies techniquement non nécessaires sur le site
        web, le fournisseur utilise un gestionnaire de cookies.
      </Typography>
      <Typography variant="body1" paragraph>
        Lors de l’ouverture du site web, un cookie contenant les informations de paramétrage est déposé
        sur le terminal de l’utilisateur, de sorte qu’il n’est pas nécessaire de demander le consentement
        lors d’une visite ultérieure. Le cookie est nécessaire pour obtenir un consentement conforme à la
        loi de la part de l’utilisateur. L’utilisateur peut, à tout moment, empêcher, voire interrompre
        l’installation de cookies en réglant les paramètres de son navigateur.
      </Typography>
      <Typography variant="body1" paragraph>
        Les consentements et les paramètres de protection des données peuvent être gérés à tout moment en
        cliquant sur le bouton ci-dessous :
      </Typography>
      <Button
        color="secondary"
        fullWidth
        variant="outlined"
        size="small"
        sx={{ mt: 3, mb: 3 }}
        onClick={openPreferences}
      >
        Adapter/révoquer les paramètres de protection des données
      </Button>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.2 Cookies
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.2.1 Cookies de session
      </Typography>
      <Typography variant="body1" paragraph>
        Nous utilisons des cookies sur notre site. Les cookies sont de petits fichiers texte, ou d’autres
        technologies de stockage, déposés et stockés sur votre appareil par le navigateur que vous
        utilisez. Ces cookies traitent certaines informations vous concernant, telles que votre
        navigateur, votre emplacement ou votre adresse IP.
      </Typography>
      <Typography variant="body1" paragraph>
        Ce traitement rend notre site plus convivial, efficace et sécurisé, car il nous permet, par
        exemple, de reproduire notre site dans différentes langues ou d’offrir une fonction de panier
        d’achat.
      </Typography>
      <Typography variant="body1" paragraph>
        La base juridique de ce traitement est l’Art. 6, paragraphe 1, point b.) du RGPD, dans la mesure
        où ces cookies sont utilisés pour traiter ou exécuter le contrat.
      </Typography>
      <Typography variant="body1" paragraph>
        Lorsque le traitement des données ne sert pas à traiter ou exécuter le contrat, notre intérêt
        légitime réside dans l’amélioration de la fonctionnalité de notre site. La base juridique est
        alors l’art. 6, paragraphe 1, point f) du RGPD.
      </Typography>
      <Typography variant="body1" paragraph>
        Lorsque vous fermez votre navigateur, les cookies sont supprimés.
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.2.2 Cookies de prestataires tiers
      </Typography>
      <Typography variant="body1" paragraph>
        Notre site utilise également, si nécessaire, des cookies de sociétés partenaires avec lesquelles
        nous coopérons à des fins de publicité, d’analyse ou de fonctionnement de notre site.
      </Typography>
      <Typography variant="body1" paragraph>
        Si vous souhaitez obtenir de plus amples informations, en particulier sur les objectifs et les
        bases juridiques du traitement de ces cookies de prestataires tiers, veuillez vous reporter aux
        informations ci-dessous.
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.2.3 Mesures de suppression
      </Typography>
      <Typography variant="body1" paragraph>
        Il est possible d’empêcher ou de limiter l’installation des cookies en paramétrant votre
        navigateur. Il est également possible de supprimer les cookies sauvegardés précédemment à tout
        moment. Les étapes et les mesures nécessaires dépendent toutefois de votre navigateur. Si vous
        vous posez des questions, utilisez la fonction d’aide ou la documentation de votre navigateur, ou
        contactez le fabricant ou l’assistance. Avec les cookies Flash, il n’est cependant pas possible
        d’empêcher le traitement en paramétrant votre navigateur. Dans ce cas, vous devez modifier les
        paramètres de votre lecteur Flash. Les étapes et les mesures nécessaires dépendent toutefois de
        votre lecteur Flash. Si vous vous posez des questions, utilisez la fonction d’aide ou la
        documentation de votre lecteur Flash, ou contactez le fabricant ou l’assistance.
      </Typography>
      <Typography variant="body1" paragraph>
        Si vous empêchez ou restreignez l’installation des cookies, il est possible que vous ne puissiez
        pas utiliser pleinement toutes les fonctions de notre site.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.3 Hosting (Vercel)
      </Typography>
      <Typography variant="body1" paragraph>
        Sur notre site web, nous utilisons Vercel, une plateforme de déploiement en nuage. Le fournisseur
        de services est l'entreprise américaine Vercel Inc, 340 S Lemon Ave #4133, Walnut, CA 91789.
      </Typography>
      <Typography variant="body1" paragraph>
        Vercel traite vos données, entre autres, aux États-Unis. Nous attirons votre attention sur le
        fait que, selon la Cour de justice européenne, il n'existe actuellement pas de niveau de
        protection adéquat pour le transfert de données vers les États-Unis. Cela peut s'accompagner de
        différents risques pour la légalité et la sécurité du traitement des données.
      </Typography>
      <Typography variant="body1" paragraph>
        Comme base du traitement des données chez des destinataires ayant leur siège dans des pays tiers
        (en dehors de l'Union européenne) ou d'un transfert de données vers ces pays, Vercel utilise ce
        que l'on appelle des clauses contractuelles standard (art. 46. al. 2 et 3 du RGPD). Les Clauses
        Contractuelles Types (CCT) sont des modèles mis à disposition par la Commission européenne et
        visent à garantir que vos données sont conformes aux normes européennes en matière de protection
        des données, même si elles sont transférées et stockées dans des pays tiers. Par ces clauses,
        Vercel s'engage à respecter le niveau européen de protection des données lors du traitement de
        vos données pertinentes, même si ces données sont stockées, traitées et gérées aux États-Unis.
        Ces clauses sont basées sur une décision d'exécution de la Commission européenne. Vous trouverez
        la décision et les clauses contractuelles types correspondantes notamment ici :
        https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj?locale=fr
      </Typography>
      <Typography variant="body1" paragraph>
        La condition de traitement des données (Data Processing Addendum), qui correspond aux clauses
        contractuelles standard, est disponible sur
        https://vercel.com/legal/Vercel_Inc_-_Data_Processing_Addendum.pdf.
      </Typography>
      <Typography variant="body1" paragraph>
        Pour en savoir plus sur les données traitées par l'utilisation de Vercel, consultez la Privacy
        Policy sur https://vercel.com/legal/privacy-policy.{" "}
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.4 Données de serveur
      </Typography>
      <Typography variant="body1" paragraph>
        Pour des raisons techniques, et notamment pour assurer la sécurité et la stabilité du site
        internet, les données nous sont transmises via votre navigateur Internet ou sont transmises à
        notre fournisseur d’espace internet. Les fichiers journaux de serveur collectent&nbsp;: le type
        et la version de votre navigateur internet, votre système d’exploitation, le site internet à
        partir duquel vous êtes arrivé sur notre site (URL de référence), le ou les pages de notre site
        que vous consultez, la date et l’heure de l’accès, et enfin, l’adresse IP de la connexion
        internet à partir de laquelle l’utilisation de notre site a eu lieu.
      </Typography>
      <Typography variant="body1" paragraph>
        Ces données collectées seront temporairement conservées, mais ne seront pas partagées avec
        d’autres données vous concernant.
      </Typography>
      <Typography variant="body1" paragraph>
        Cette conservation est effectuée selon l’Art. 6, paragraphe 1, point f) du RGPD. Notre intérêt
        est légitime car ces données participent à l’amélioration, la stabilité, la fonctionnalité et la
        sécurité de notre site.
      </Typography>
      <Typography variant="body1" paragraph>
        Les données seront supprimées au plus tard au bout de sept jours, sauf si nous devons les
        conserver plus longtemps à titre probatoire. Dans ce cas, tout ou partie des données sera
        conservé jusqu’au règlement définitif du problème.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.5 Facebook
      </Typography>
      <Typography variant="body1" paragraph>
        Afin d’assurer la publicité relative à nos produits et prestations, de même qu’afin de
        communiquer avec des prospects ou clients, nous veillons à ce que notre entreprise dispose d’une
        certaine visibilité sur la plateforme Facebook.
      </Typography>
      <Typography variant="body1" paragraph>
        Sur la plateforme de ce réseau social, nous sommes conjointement responsables avec la Société
        Meta Platforms Ireland Limited, 4 Grand Canal Square, Dublin 2, Irland.
      </Typography>
      <Typography variant="body1" paragraph>
        Le Délégué à la protection des données de Facebook peut être contacté via le formulaire suivant :
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://www.facebook.com/help/contact/540977946302970"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://www.facebook.com/help/contact/540977946302970
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        Nous avons fixé les dispositions applicables à cette coresponsabilité dans un accord portant sur
        les différentes obligations découlant du RGPD. Ledit accord, duquel découlent certaines
        obligations réciproques, est consultable via le lien suivant :
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://www.facebook.com/legal/terms/page_controller_addendum"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://www.facebook.com/legal/terms/page_controller_addendum
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        La base juridique régissant les opérations de traitement des données personnelles ainsi initiées
        et décrites ci-après est l’Art. 6 paragraphe 1 point f du RGPD. L’analyse, la communication, de
        même que la commercialisation de nos produits et services et la poursuite d’activités
        publicitaires concernant ces derniers constituent notre intérêt légitime au sens de ce règlement.
      </Typography>
      <Typography variant="body1" paragraph>
        Le consentement de l’utilisateur, tel que défini à l’art. 6 paragraphe 1 point a du RGPD,
        vis-à-vis de l’exploitant de la plateforme peut également servir de base juridique régissant ces
        opérations. L’utilisateur dispose du droit de retirer son consentement à tout moment, avec effet
        ultérieur, conformément à l’Art. 7 paragraphe 3 du RGPD, en notifiant l’exploitant de la
        plateforme de cette décision.
      </Typography>
      <Typography variant="body1" paragraph>
        Lors de la consultation de notre site Internet sur la plateforme Facebook, certaines données
        relatives à l’utilisateur (par exemple, informations personnelles, adresse IP, etc.) seront
        traitées par Facebook Ireland Ltd., en tant que société d’exploitation de la plateforme au sein
        de l’UE.
      </Typography>
      <Typography variant="body1" paragraph>
        Les données de l’utilisateur sont utilisées à des fins statistiques, en vue de collecter des
        informations concernant l’impact de la visibilité de notre entreprise sur Facebook. La Société
        Facebook Ireland Ltd. utilise ces données à des fins publicitaires ou d’études de marché, ainsi
        qu’en vue d’établir des profils d’utilisateurs. Grâce aux profils ainsi établis, Facebook Ireland
        Ltd. peut notamment diffuser des publicités auprès de ces utilisateurs, en fonction de leurs
        intérêts, sur Facebook comme en dehors de cette plateforme. Dans le cas où l’utilisateur, au
        moment de sa visite, est déjà connecté sur son compte Facebook, Facebook Ireland Ltd. peut en
        outre associer ces données avec le compte utilisateur correspondant.
      </Typography>
      <Typography variant="body1" paragraph>
        Dans le cas d’une prise de contact de l’utilisateur sur Facebook, les données personnelles le
        concernant saisies à cette occasion seront utilisées afin de traiter la demande. Nous assurerons
        la suppression de ces données utilisateur, dès lors que la demande émanant de l’utilisateur sera
        considérée dûment traitée, et dans la mesure où aucune obligation légale de conservation, par
        exemple dans le cas de l’exécution ultérieure d’un contrat, ne s’y oppose.
      </Typography>
      <Typography variant="body1" paragraph>
        Afin de procéder au traitement des données, des cookies sont susceptibles d’être installés, le
        cas échéant, par la Société Facebook Ireland Ltd.
      </Typography>
      <Typography variant="body1" paragraph>
        Dans le cas où l’utilisateur ne souhaiterait pas que ce traitement soit effectué, il est possible
        d’empêcher l’installation des cookies en paramétrant le navigateur Internet à cet effet. Les
        cookies déjà enregistrés peuvent également être effacés à tout moment. Les réglages prévus à cet
        effet dépendent du navigateur utilisé. Pour ce qui est des cookies Flash, étant donné qu’il est
        impossible d’en empêcher le traitement en agissant sur les paramètres du navigateur Internet, il
        convient de modifier les paramètres du lecteur Flash correspondants. Dans le cas où l’utilisateur
        empêche ou limite l’installation de cookies, il est possible que celui-ci ne puisse plus, par la
        suite, utiliser pleinement toutes les fonctionnalités de Facebook.
      </Typography>
      <Typography variant="body1" paragraph>
        La Politique d’utilisation des données de Facebook comprend certaines informations
        complémentaires sur les opérations de traitement des données, de même que leur interruption,
        ainsi que la suppression des données traitées par Facebook :
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://www.facebook.com/privacy/explanation"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://www.facebook.com/privacy/explanation
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        Il n’est pas exclu qu’un traitement des données ait lieu par l’intermédiaire de la Société
        Facebook Ireland Ltd., éventuellement via la Société Meta Platforms, Inc., 1601 Willow Road,
        Menlo Park, California 94025, aux États-Unis.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.6 Instagram
      </Typography>
      <Typography variant="body1" paragraph>
        Afin d’assurer la publicité relative à nos produits et prestations, de même qu’afin de
        communiquer avec des prospects ou clients, nous veillons à ce que notre entreprise dispose d’une
        certaine visibilité sur la plateforme Instagram.
      </Typography>
      <Typography variant="body1" paragraph>
        Sur la plateforme de ce réseau social, nous sommes conjointement responsables avec la Société
        Meta Platforms Ireland Limited, 4 Grand Canal Square, Dublin 2, Irlande.
      </Typography>
      <Typography variant="body1" paragraph>
        Le Délégué à la protection des données de Instagram peut être contacté via le formulaire suivant
        :
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://www.facebook.com/help/contact/540977946302970"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://www.facebook.com/help/contact/540977946302970
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        Nous avons fixé les dispositions applicables à cette coresponsabilité dans un accord portant sur
        les différentes obligations découlant du RGPD. Ledit accord, duquel découlent certaines
        obligations réciproques, est consultable via le lien suivant :
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://www.facebook.com/legal/terms/page_controller_addendum"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://www.facebook.com/legal/terms/page_controller_addendum
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        La base juridique régissant les opérations de traitement des données personnelles ainsi initiées
        et décrites ci-après est l’Art. 6 paragraphe 1 point f du RGPD. L’analyse, la communication, de
        même que la commercialisation de nos produits et services et la poursuite d’activités
        publicitaires concernant ces derniers constituent notre intérêt légitime au sens de ce règlement.
      </Typography>
      <Typography variant="body1" paragraph>
        Le consentement de l’utilisateur, tel que défini à l’art. 6 paragraphe 1 point a du RGPD,
        vis-à-vis de l’exploitant de la plateforme peut également servir de base juridique régissant ces
        opérations. L’utilisateur dispose du droit de retirer son consentement à tout moment, avec effet
        ultérieur, conformément à l’Art. 7 paragraphe 3 du RGPD, en notifiant l’exploitant de la
        plateforme de cette décision.
      </Typography>
      <Typography variant="body1" paragraph>
        Lors de la consultation de notre site Internet sur la plateforme Instagram, certaines données
        relatives à l’utilisateur (par exemple, informations personnelles, adresse IP, etc.) seront
        traitées par Facebook Ireland Ltd., en tant que société d’exploitation de la plateforme au sein
        de l’UE.
      </Typography>
      <Typography variant="body1" paragraph>
        Les données de l’utilisateur sont utilisées à des fins statistiques, en vue de collecter des
        informations concernant l’impact de la visibilité de notre entreprise sur Instagram. La Société
        Facebook Ireland Ltd. utilise ces données à des fins publicitaires ou d’études de marché, ainsi
        qu’en vue d’établir des profils d’utilisateurs. Grâce aux profils ainsi établis, Facebook Ireland
        Ltd. peut notamment diffuser des publicités auprès de ces utilisateurs, en fonction de leurs
        intérêts, sur Instagram comme en dehors de cette plateforme. Dans le cas où l’utilisateur, au
        moment de sa visite, est déjà connecté sur son compte Instagram, Facebook Ireland Ltd. peut en
        outre associer ces données avec le compte utilisateur correspondant.
      </Typography>
      <Typography variant="body1" paragraph>
        Dans le cas d’une prise de contact de l’utilisateur sur Instagram, les données personnelles le
        concernant saisies à cette occasion seront utilisées afin de traiter la demande. Nous assurerons
        la suppression de ces données utilisateur, dès lors que la demande émanant de l’utilisateur sera
        considérée dûment traitée, et dans la mesure où aucune obligation légale de conservation, par
        exemple dans le cas de l’exécution ultérieure d’un contrat, ne s’y oppose.
      </Typography>
      <Typography variant="body1" paragraph>
        Afin de procéder au traitement des données, des cookies sont susceptibles d’être installés, le
        cas échéant, par la Société Facebook Ireland Ltd.
      </Typography>
      <Typography variant="body1" paragraph>
        Dans le cas où l’utilisateur ne souhaiterait pas que ce traitement soit effectué, il est possible
        d’empêcher l’installation des cookies en paramétrant le navigateur Internet à cet effet. Les
        cookies déjà enregistrés peuvent également être effacés à tout moment. Les réglages prévus à cet
        effet dépendent du navigateur utilisé. Pour ce qui est des cookies Flash, étant donné qu’il est
        impossible d’en empêcher le traitement en agissant sur les paramètres du navigateur Internet, il
        convient de modifier les paramètres du lecteur Flash correspondants. Dans le cas où l’utilisateur
        empêche ou limite l’installation de cookies, il est possible que celui-ci ne puisse plus, par la
        suite, utiliser pleinement toutes les fonctionnalités de Instagram.
      </Typography>
      <Typography variant="body1" paragraph>
        La Politique d’utilisation des données de Instagram comprend certaines informations
        complémentaires sur les opérations de traitement des données, de même que leur interruption,
        ainsi que la suppression des données traitées par Instagram:
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://help.instagram.com/519522125107875"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://help.instagram.com/519522125107875
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        Il n’est pas exclu qu’un traitement des données ait lieu par l’intermédiaire de la Société
        Facebook Ireland Ltd., éventuellement via la Société Meta Platforms, Inc., 1601 Willow Road,
        Menlo Park, California 94025, aux États-Unis.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.7 Pinterest
      </Typography>
      <Typography variant="body1" paragraph>
        Nous assurons, sur Pinterest, une présence en ligne, afin de présenter notre entreprise, ainsi
        que nos prestations, et de communiquer auprès de clients/prospects. Pinterest est un service de
        Pinterest Inc., 651 Brannan Street, San Francisco, CA, 94107, États-Unis.
      </Typography>
      <Typography variant="body1" paragraph>
        Dans cette mesure, nous vous rappelons que certaines données relatives à l’utilisateur sont
        susceptibles d’être traitées en dehors de l’Union européenne, et notamment aux États-Unis. Cela
        peut entraîner des risques accrus pour l’utilisateur, dans la mesure où l’accès ultérieur à ses
        données utilisateur pourrait s’avérer plus complexe. Nous n’avons pas, non plus, accès à ces
        données utilisateur. L’accès à celles-ci est en effet exclusivement réservé à Pinterest.
      </Typography>
      <Typography variant="body1" paragraph>
        Les consignes de Pinterest en matière de protection des données sont consultables sur :
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://policy.pinterest.com/fr/privacy-policy"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://policy.pinterest.com/fr/privacy-policy
        </a>
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.8 YouTube
      </Typography>
      <Typography variant="body1" paragraph>
        Nous assurons, sur Youtube, une présence en ligne, afin de présenter notre entreprise, ainsi que
        nos prestations, et de communiquer auprès de clients/prospects. YouTube est un service de la
        Société Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irlande, filiale de Google
        LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, États-Unis.
      </Typography>
      <Typography variant="body1" paragraph>
        Dans cette mesure, nous vous rappelons que certaines données relatives à l’utilisateur sont
        susceptibles d’être traitées en dehors de l’Union européenne, et notamment aux États-Unis. Cela
        peut entraîner des risques accrus pour l’utilisateur, dans la mesure où l’accès ultérieur à ses
        données utilisateur pourrait s’avérer plus complexe. Nous n’avons pas, non plus, accès à ces
        données utilisateur. L’accès à celles-ci est en effet exclusivement réservé à YouTube.
      </Typography>
      <Typography variant="body1" paragraph>
        Les consignes de Youtube en matière de protection des données sont consultables sur :
      </Typography>
      <Typography variant="body1" paragraph>
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener nofollow noreferrer">
          https://policies.google.com/privacy
        </a>
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.9 Liens généraux vers des profils de tiers
      </Typography>
      <Typography variant="body1" paragraph>
        Le fournisseur met en place sur le site web un lien vers les réseaux sociaux mentionnés
        ci-dessous.
      </Typography>
      <Typography variant="body1" paragraph>
        La base juridique est ici l'art. 6, alinéa 1, lettre f du RGPD. L'intérêt légitime du fournisseur
        réside dans l'amélioration de la qualité d'utilisation du site web.
      </Typography>
      <Typography variant="body1" paragraph>
        L'intégration des plugins se fait par le biais d'un graphique lié. Ce n'est qu'en cliquant sur le
        graphique correspondant que l'utilisateur est redirigé vers le service du réseau social concerné.
      </Typography>
      <Typography variant="body1" paragraph>
        Une fois le client redirigé, le réseau concerné saisit des informations sur l'utilisateur. Il
        s'agit tout d'abord de données telles que l'adresse IP, la date, l'heure et la page visitée. Si
        l'utilisateur est connecté à son compte d'utilisateur du réseau concerné pendant ce temps,
        l'exploitant du réseau peut, le cas échéant, attribuer les informations collectées de la visite
        concrète de l'utilisateur au compte personnel de l'utilisateur. Si l'utilisateur interagit via un
        bouton "Partager" du réseau concerné, ces informations peuvent être enregistrées dans le compte
        d'utilisateur personnel de l'utilisateur et, le cas échéant, publiées. Si l'utilisateur souhaite
        empêcher que les informations collectées soient directement attribuées à son compte utilisateur,
        il doit se déconnecter avant de cliquer sur le graphique. Il est en outre possible de configurer
        le compte d'utilisateur concerné en conséquence.
      </Typography>
      <Typography variant="body1" paragraph>
        Les réseaux sociaux suivants sont liés par le fournisseur :
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.9.1 Facebook
      </Typography>
      <Typography variant="body1" paragraph>
        Facebook Ireland Limited, 4 Grand Canal Square, Dublin 2, Irlande, filiale de Facebook Inc., 1601
        S. California Avenue, Palo Alto, CA 94304, États-Unis.
      </Typography>
      <Typography variant="body1" paragraph>
        Politique d’utilisation des données :{" "}
        <a href="https://www.facebook.com/policy.php" target="_blank" rel="noopener nofollow noreferrer">
          https://www.facebook.com/policy.php
        </a>
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.9.2 Instagram
      </Typography>
      <Typography variant="body1" paragraph>
        Facebook Ireland Limited, 4 Grand Canal Square, Dublin 2, Irlande, filiale de Facebook Inc., 1601
        S. California Avenue, Palo Alto, CA 94304, États-Unis.
      </Typography>
      <Typography variant="body1" paragraph>
        Politique d’utilisation des données:
        <a
          href="https://help.instagram.com/519522125107875"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://help.instagram.com/519522125107875
        </a>
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.9.3 Pinterest
      </Typography>
      <Typography variant="body1" paragraph>
        Pinterest Inc., 651 Brannan Street, San Francisco, CA, 94107, USA.
      </Typography>
      <Typography variant="body1" paragraph>
        Politique d’utilisation des données:
        <a
          href="https://policy.pinterest.com/fr/privacy-policy"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://policy.pinterest.com/fr/privacy-policy
        </a>
      </Typography>
      <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
        3.9.4 YouTube
      </Typography>
      <Typography variant="body1" paragraph>
        Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland, ein Tochterunternehmen der
        Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043 USA
      </Typography>{" "}
      <Typography variant="body1" paragraph>
        Politique d’utilisation des données :
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener nofollow noreferrer">
          https://policies.google.com/privacy
        </a>
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.10 Google Analytics
      </Typography>
      <Typography variant="body1" paragraph>
        Nous utilisons Google Analytics sur notre site internet. Google Analytics est un service
        d’analyse internet fourni par Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
        Irland, ci-après dénommé «&nbsp;Google&nbsp;».
      </Typography>
      <Typography variant="body1" paragraph>
        Le service Google Analytics est utilisé pour analyser le comportement de l’utilisateur sur notre
        site La base juridique est l’Art. 6, paragraphe 1, point f) du RGPD. Notre intérêt légitime
        réside dans l’analyse, l’optimisation et une exploitation rentable de notre site.
      </Typography>
      <Typography variant="body1" paragraph>
        Les informations relatives à l’utilisation et aux utilisateurs, telles que l’adresse IP,
        l’emplacement, l’heure ou la fréquence des visites sur notre site, sont transmises à un serveur
        de Google aux États-Unis et y sont stockées. Cependant, nous utilisons Google Analytics avec la
        fonction d’anonymisation. Cette fonction permet à Google de tronquer l’adresse IP en UE et dans
        l’EEE.
      </Typography>
      <Typography variant="body1" paragraph>
        Les données ainsi collectées seront de nouveau utilisées par Google pour nous fournir une
        évaluation de la visite de notre site, ainsi que des activités d’utilisation sur ce site. En
        outre, ces données peuvent être utilisées pour fournir d’autres services liés à l’utilisation de
        notre site et à l’utilisation d’Internet.
      </Typography>
      <Typography variant="body1" paragraph>
        Google déclare que votre adresse IP n’est pas associée à d’autres données. En outre, Google
        conserve pour vous à l’adresse&nbsp;:
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://www.google.com/intl/fr/policies/privacy/partners"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://www.google.com/intl/fr/policies/privacy/partners
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        d’autres informations relatives à la protection des données, notamment la possibilité d’empêcher
        l’utilisation de données.
      </Typography>
      <Typography variant="body1" paragraph>
        De plus, Google propose à l’adresse&nbsp;:
      </Typography>
      <Typography variant="body1" paragraph>
        <a
          href="https://tools.google.com/dlpage/gaoptout?hl=fr"
          target="_blank"
          rel="noopener nofollow noreferrer"
        >
          https://tools.google.com/dlpage/gaoptout?hl=de
        </a>
      </Typography>
      <Typography variant="body1" paragraph>
        une extension complémentaire de désactivation comprenant des informations supplémentaires. Cette
        extension complémentaire peut être installée avec les navigateurs internet les plus courants et
        vous offre un contrôle supplémentaire des données collectées par Google lorsque vous consultez
        notre site. L’extension complémentaire informe le JavaScript (ga.js) de Google Analytics que les
        informations permettant de consulter notre site ne doivent pas être transmises à Google
        Analytics. Cela n’empêche pas que les informations peuvent nous être transmises ou être
        transmises à d’autres services d’analyse de sites internet. Vous serez bien évidemment informé
        par cette Politique de confidentialité des éventuels autres services d’analyse de sites internet
        que nous utilisons.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.11 CloudFront / S3
      </Typography>
      <Typography variant="body1" paragraph>
        Pour sécuriser notre site web et optimiser les temps de chargement, nous utilisons les services
        AWS CloudFront et AWS S3. Il s'agit de services de stockage de fichiers et d'un CDN (Content
        Delivery Network). Les services sont fournis par Amazon Web Services EMEA SARL, 38 avenue John F.
        Kennedy, L-1855 Luxembourg.
      </Typography>{" "}
      <Typography variant="body1" paragraph>
        La base juridique est l’Art. 6, paragraphe 1, point f) du RGPD. Notre intérêt légitime réside
        dans l’optimisation et le bon fonctionnement de notre site.
      </Typography>{" "}
      <Typography variant="body1" paragraph>
        Si vous consultez notre site, vos demandes seront acheminées via le serveur de CloudFront. Dans
        ce cas, des données d’accès statistiques sont collectées lorsque vous consultez notre site et un
        cookie est stocké sur votre appareil par CloudFlare via votre navigateur internet. Les données
        recueillies sont&nbsp;:
      </Typography>
      <Box component="ul" sx={{ pl: 3 }}>
        <Box component="li">votre adresse IP,</Box>
        <Box component="li">la(s) page(s) de notre site consultée(s),</Box>
        <Box component="li">le type et la version du navigateur utilisé,</Box>
        <Box component="li">le système d’exploitation utilisé,</Box>
        <Box component="li">
          le site à partir duquel vous avez été redirigé sur notre site internet (URL de référence),
        </Box>
        <Box component="li">la durée de votre visite sur notre site et</Box>
        <Box component="li">la fréquence d’appel de nos pages.</Box>
      </Box>
      <Typography variant="body1" paragraph>
        CloudFront utilise ces données à des fins d’analyse statistique de l’accès, ainsi que dans le
        cadre de la sécurité et de l’optimisation de l’offre.
      </Typography>
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.12 YouTube
      </Typography>
      <Typography variant="body1" paragraph>
        Nous utilisons YouTube sur notre site internet. Nous faisons ici référence à un portail vidéo de
        Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland, ci-après dénommé « YouTube
        ».
      </Typography>
      <Typography variant="body1" paragraph>
        Pour vous proposer des vidéos, nous utilisons YouTube et la fonction de « mode de protection des
        données avancé ». Pour tout consentement que vous donnez à ce traitement, le fondement juridique
        est l’art. 6, paragraphe 1, point a du RGPD. La fonction de « mode de protection des données
        renforcé » signifie, selon YouTube, que les données ci-dessous ne sont pas transmises au serveur
        de YouTube, sauf si vous lisez réellement une vidéo.
      </Typography>
      <Typography variant="body1" paragraph>
        Sans cette «&nbsp;protection des données renforcée », vous seriez connecté au serveur YouTube aux
        États-Unis dès lors que vous visiteriez l’une de nos pages contenant une vidéo YouTube.
      </Typography>
      <Typography variant="body1" paragraph>
        Cette connexion est nécessaire pour pouvoir afficher la vidéo correspondante sur notre site via
        votre navigateur internet. Pour ce faire, YouTube collecte et traite au moins votre adresse IP,
        la date et l’heure, ainsi que la page que vous consultez. Il se connecte également au réseau
        publicitaire « DoubleClick&nbsp;» de Google.
      </Typography>
      <Typography variant="body1" paragraph>
        Si vous êtes connecté simultanément à YouTube, ce dernier fournit les informations de connexion à
        votre compte YouTube. Si vous souhaitez éviter que cela ne se produise, vous devez soit vous
        déconnecter de YouTube avant de consulter notre site, soit définir les paramètres appropriés dans
        votre compte utilisateur YouTube.
      </Typography>
      <Typography variant="body1" paragraph>
        Pour des raisons de fonctionnalité et pour l’analyse du comportement d’utilisation, YouTube
        enregistre en permanence les cookies via votre navigateur internet sur votre appareil. Si vous ne
        souhaitez pas que ce traitement soit effectué, vous pouvez empêcher l’enregistrement des cookies
        en paramétrant votre navigateur Internet à cet effet. Pour obtenir des informations
        supplémentaires, veuillez consulter la rubrique «&nbsp;Cookies&nbsp;» ci-dessus.
      </Typography>
      <Typography variant="body1" paragraph>
        Pour obtenir des informations supplémentaires sur la collecte et l’utilisation de données, ainsi
        que sur vos droits et vos options de protection, consultez la politique de confidentialité de
        Google à l’adresse&nbsp;:
      </Typography>
      <Typography variant="body1" paragraph>
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener nofollow noreferrer">
          https://policies.google.com/privacy
        </a>
      </Typography>{" "}
      <Typography variant="h3" sx={{ pt: 4, pb: 3 }}>
        3.13 Assistance à la clientèle et support
      </Typography>
      <Typography variant="body1" paragraph>
        Pour le service clientèle, tu peux nous contacter par e-mail. Nous indiquons à cet effet une
        adresse e-mail correspondante aux endroits appropriés sur les pages de notre boutique en ligne.
        Les données personnelles que tu nous communiques lors d'une demande par e-mail (par ex. nom,
        adresse e-mail, numéro de téléphone) ne sont traitées que pour correspondre avec toi et
        uniquement dans le but pour lequel tu nous as fourni ces données.
      </Typography>
      <Typography variant="body1" paragraph>
        En outre, nous mettons à disposition sur notre site Internet la possibilité de télécharger du
        matériel supplémentaire pour les marchandises que nous vendons. Pour utiliser cette possibilité,
        tu as besoin d'un code de support. Ce code est fourni avec chacun de nos produits et vise à
        garantir que seuls les propriétaires de nos produits peuvent utiliser cette possibilité. Il n'est
        pas possible ni prévu que le code d'assistance permette de remonter jusqu'à ta personne.
      </Typography>
      <Typography variant="body1" paragraph>
        La base juridique pour le traitement des données est l’Art. 6, paragraphe 1, point b.) du RGPD.
      </Typography>
      <Typography variant="body1" paragraph>
        Basé sur : 
        <a
          href="https://www.generator-datenschutzerklärung.de"
          target="_blank"
          rel="noopener noreferrer"
        >
          Exemple de politique de confidentialité
        </a>{" "}
        du{" "}
        <a href="https://www.ratgeberrecht.eu/" target="_blank" rel="noopener noreferrer">
          cabinet d’avocats Weiß &amp; Partner
        </a>
      </Typography>
    </Container>
  );
};

PrivacyPolicy.getLayout = (page) => (
  <BaseLayout
    title="Datenschutzerklärung | Akte Mord®"
    description="Informationen welche personenbezogenen Daten wir bei der Nutzung unseres Online-Shops erheben und verarbeiten."
    noindex={true}
  >
    {page}
  </BaseLayout>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale
        ? await serverSideTranslations(locale, ["c-common", "c-user-consent-management"], i18nConfig)
        : {}),
    },
  };
};

export default PrivacyPolicy;
